/**
 * One path builder for every line on the canvas — attached edges and free
 * connectors. Three routings:
 *
 * - straight: a direct line. Only its ends can be moved.
 * - curved: React Flow's bezier by default; with a stored `bend` (an offset
 *   from the straight midpoint) the curve passes through that point instead,
 *   which is where the single bend handle sits.
 * - step (Elbow): an orthogonal polyline. React Flow's smooth-step route by
 *   default; with stored `points` the line runs through them. The line is
 *   read as straight runs — maximal collinear stretches between the two
 *   leaving stubs — and each run wears one bar that slides the whole run
 *   along one axis, the way Figma's connectors work. The automatic route is
 *   the React Flow Pro editable-edge example's, ported.
 *
 * Everything the callers need comes back together: the SVG path, the label
 * point, and the handle positions, so nothing re-derives the midpoint.
 */

import {
  getBezierPath,
  getStraightPath,
  Position,
  type XYPosition,
} from "@xyflow/react";

export type LineCurve = "straight" | "smooth" | "step";

export type LinePoint = XYPosition & { id: string };

/** A bar on a straight run of an elbow line: drag it along `axis`. */
export type LineStepAnchor = XYPosition & {
  id: string;
  axis: "x" | "y";
  /** The interior points the run spans, inclusive; all slide together. */
  first: number;
  last: number;
};

export interface LineEnd extends XYPosition {
  side: Position;
}

/** Where an edge's ends were when its route was stored. */
export interface LineRouteFrame {
  source: XYPosition;
  target: XYPosition;
}

export interface LineGeometry {
  curve: LineCurve;
  source: LineEnd;
  target: LineEnd;
  /** Curved: offset of the through-point from the straight midpoint. */
  bend?: XYPosition | null;
  /** Elbow: the user's interior points, in order from source to target. */
  points?: LinePoint[] | null;
  /** Elbow, edges: where the ends were when `points` was stored. */
  pointsFrame?: LineRouteFrame | null;
  /** Elbow: a label sits on the line, so the bar on its run steps aside. */
  labelled?: boolean;
}

export interface LinePath {
  path: string;
  label: XYPosition;
  /** Curved only: where the bend handle sits (the through-point). */
  bendHandle: XYPosition | null;
  /** The highest y the line reaches; the bar sits above it. */
  top: number;
  /** Elbow only: the bars. */
  stepAnchors: LineStepAnchor[];
  /**
   * Elbow only: the interior points as drawn — re-seated on the current node
   * positions and made orthogonal. A drag starts from these.
   */
  stepPoints: LinePoint[];
}

export const STEP_OFFSET = 20;

export function buildLinePath(g: LineGeometry): LinePath {
  const { source, target } = g;
  if (g.curve === "straight") {
    const [path, labelX, labelY] = getStraightPath({
      sourceX: source.x,
      sourceY: source.y,
      targetX: target.x,
      targetY: target.y,
    });
    return {
      path,
      label: { x: labelX, y: labelY },
      bendHandle: null,
      top: Math.min(source.y, target.y),
      stepAnchors: [],
      stepPoints: [],
    };
  }

  if (g.curve === "smooth") {
    const mid = { x: (source.x + target.x) / 2, y: (source.y + target.y) / 2 };
    if (g.bend && (g.bend.x !== 0 || g.bend.y !== 0)) {
      // A quadratic through point B has control C = 2B - (S + T) / 2.
      const b = { x: mid.x + g.bend.x, y: mid.y + g.bend.y };
      const c = { x: 2 * b.x - mid.x, y: 2 * b.y - mid.y };
      return {
        path: `M ${source.x} ${source.y} Q ${c.x} ${c.y} ${target.x} ${target.y}`,
        label: b,
        bendHandle: b,
        top: Math.min(source.y, target.y, b.y),
        stepAnchors: [],
        stepPoints: [],
      };
    }
    const [path, labelX, labelY] = getBezierPath({
      sourceX: source.x,
      sourceY: source.y,
      sourcePosition: source.side,
      targetX: target.x,
      targetY: target.y,
      targetPosition: target.side,
    });
    return {
      path,
      label: { x: labelX, y: labelY },
      bendHandle: { x: labelX, y: labelY },
      top: Math.min(source.y, target.y, labelY),
      stepAnchors: [],
      stepPoints: [],
    };
  }

  // Elbow. The automatic route and a hand-drawn one go through the same
  // builder, so touching a line never changes how it is drawn.
  const stored = g.points ?? [];
  const handRouted = stored.length > 0;
  const interior = handRouted
    ? resolveStepPoints(routeAt(stored, g), g)
    : getStepInitialPoints(g).slice(1, -1);
  const full = [source, ...interior, target];
  const runs = stepRuns(interior);
  // The label sits in the middle of the run that contains the half-way
  // point of the line (Figma's rule): the middle run, centred, even when
  // the half-way point itself lies near one of that run's ends. Full-
  // polyline segment i joins interior points i-1 and i; when the half-way
  // point falls on a stub, the label centres on that stub instead.
  const middle = pathMidpoint(full);
  const labelRun =
    runs.find(
      (run) => run.first <= middle.segment - 1 && middle.segment <= run.last,
    ) ?? null;
  const label = labelRun
    ? midpoint(labelRun.a, labelRun.b)
    : midpoint(full[middle.segment], full[middle.segment + 1]);
  return {
    path: getStepPath(full),
    label,
    bendHandle: null,
    top: Math.min(...full.map((p) => p.y)),
    stepAnchors: stepAnchorsFor(
      runs,
      handRouted,
      labelRun,
      label,
      !!g.labelled,
    ),
    stepPoints: interior,
  };
}

// --- Elbow: runs and bars ---------------------------------------------------

/**
 * A straight run of the interior polyline: the stretch from interior point
 * `first` to `last` that lies on one line. Duplicate points inside it are
 * fine; a run is what the eye sees as one segment.
 */
interface StepRun {
  axis: "x" | "y";
  first: number;
  last: number;
  a: LinePoint;
  b: LinePoint;
}

function stepRuns(interior: LinePoint[]): StepRun[] {
  const runs: StepRun[] = [];
  let open: StepRun | null = null;
  for (let i = 0; i < interior.length - 1; i++) {
    const p = interior[i];
    const q = interior[i + 1];
    const horizontal = p.y === q.y && p.x !== q.x;
    const vertical = p.x === q.x && p.y !== q.y;
    if (!horizontal && !vertical) {
      // A duplicate point: it belongs to whichever run is open.
      if (open) {
        open.last = i + 1;
        open.b = q;
      }
      continue;
    }
    // A horizontal run slides along y; a vertical one along x.
    const axis: "x" | "y" = horizontal ? "y" : "x";
    if (open && open.axis === axis) {
      open.last = i + 1;
      open.b = q;
      continue;
    }
    open = { axis, first: i, last: i + 1, a: p, b: q };
    runs.push(open);
  }
  return runs;
}

function longestRun(runs: StepRun[]): StepRun | null {
  let best: StepRun | null = null;
  let bestLen = -1;
  for (const run of runs) {
    const len = dist(run.a, run.b);
    if (len > bestLen) {
      bestLen = len;
      best = run;
    }
  }
  return best;
}

function midpoint(a: XYPosition, b: XYPosition): XYPosition {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/** The point half-way along a polyline, and the segment it lies on. */
interface PathMidpoint {
  point: XYPosition;
  segment: number;
}

function pathMidpoint(full: XYPosition[]): PathMidpoint {
  const lengths = full.slice(1).map((p, i) => dist(full[i], p));
  let remaining = lengths.reduce((sum, l) => sum + l, 0) / 2;
  for (let i = 0; i < lengths.length; i++) {
    if (remaining <= lengths[i] || i === lengths.length - 1) {
      const t = lengths[i] > 0 ? remaining / lengths[i] : 0;
      const a = full[i];
      const b = full[i + 1];
      return {
        point: { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t },
        segment: i,
      };
    }
    remaining -= lengths[i];
  }
  return { point: { x: full[0].x, y: full[0].y }, segment: 0 };
}

/** A run shorter than this gets no bar: nothing to grab. */
const MIN_ANCHOR_RUN = 12;

/**
 * The bars. An untouched line offers one, at the middle of the line; a
 * hand-routed line offers one per run long enough to grab. When a label
 * sits on a run, that run's bar becomes two, one in the middle of each
 * half either side of the label — and a half too short to grab gets none,
 * so a bar never lands on the label (Figma hides them the same way).
 */
function stepAnchorsFor(
  runs: StepRun[],
  handRouted: boolean,
  labelRun: StepRun | null,
  labelAt: XYPosition,
  labelled: boolean,
): LineStepAnchor[] {
  const anchors: LineStepAnchor[] = [];
  const grabbable = (a: XYPosition, b: XYPosition) =>
    dist(a, b) >= MIN_ANCHOR_RUN;
  // The middle can fall on a stub; then the longest run takes the one bar.
  const single = handRouted ? null : (labelRun ?? longestRun(runs));
  for (const run of runs) {
    if (!handRouted && run !== single) continue;
    const base = { axis: run.axis, first: run.first, last: run.last };
    if (labelled && run === labelRun) {
      if (grabbable(run.a, labelAt)) {
        anchors.push({
          ...base,
          id: `${run.a.id}-a`,
          ...midpoint(run.a, labelAt),
        });
      }
      if (grabbable(labelAt, run.b)) {
        anchors.push({
          ...base,
          id: `${run.a.id}-b`,
          ...midpoint(labelAt, run.b),
        });
      }
      continue;
    }
    if (!grabbable(run.a, run.b)) continue;
    const at =
      !handRouted && run === labelRun ? labelAt : midpoint(run.a, run.b);
    anchors.push({ ...base, id: run.a.id, ...at });
  }
  return anchors;
}

/**
 * Start dragging a run. Returns the interior points to drag from and the
 * run's span within them. The first and last interior points are the stubs
 * that keep the line leaving each node straight; a run that reaches a stub
 * is split off it here, once, so the stub stays put and the moved run turns
 * the corner on its own.
 */
export function beginStepRunDrag(
  g: LineGeometry,
  built: LinePath,
  anchor: LineStepAnchor,
): { points: LinePoint[]; first: number; last: number } {
  const points =
    built.stepPoints.length > 0
      ? built.stepPoints.map((p) => ({ ...p, id: p.id || pointId() }))
      : getStepInitialPoints(g)
          .slice(1, -1)
          .map((p) => ({ ...p, id: pointId() }));
  let { first, last } = anchor;
  if (first === 0) {
    points.unshift({ ...points[0], id: pointId() });
    first += 1;
    last += 1;
  }
  if (last === points.length - 1) {
    points.push({ ...points[points.length - 1], id: pointId() });
  }
  return { points, first, last };
}

/** Slide the run `first..last` along `axis` to `value`; returns new points. */
export function moveStepRun(
  points: LinePoint[],
  first: number,
  last: number,
  axis: "x" | "y",
  value: number,
): LinePoint[] {
  return points.map((p, i) =>
    i >= first && i <= last ? { ...p, [axis]: value } : { ...p },
  );
}

/**
 * Where a dragged run settles. Within `tolerance` of a guide it takes the
 * guide: the ends and the midpoint between them, the automatic route, and
 * the corners either side of the run (meeting one straightens the jog).
 * Off every guide it takes the grid, so a hand-drawn line is never a stray
 * couple of pixels off.
 */
export function snapStepValue(
  g: LineGeometry,
  points: LinePoint[],
  first: number,
  last: number,
  axis: "x" | "y",
  value: number,
  tolerance: number,
  grid: number,
): number {
  const guides = [
    g.source[axis],
    g.target[axis],
    (g.source[axis] + g.target[axis]) / 2,
    ...getStepInitialPoints(g).map((p) => p[axis]),
    points[first - 1]?.[axis],
    points[last + 1]?.[axis],
  ].filter((v): v is number => v !== undefined);
  let best = value;
  let bestDist = tolerance;
  for (const guide of guides) {
    const d = Math.abs(guide - value);
    if (d < bestDist) {
      bestDist = d;
      best = guide;
    }
  }
  if (best !== value) return best;
  return Math.round(value / grid) * grid;
}

/**
 * An edge stores its corners in canvas coordinates together with where its
 * ends were at the time (the frame). When both ends have since moved by the
 * same amount — a section, a multi-selection — the corners move with them
 * exactly. When one end moved on its own, the corners stay and only the
 * segments nearest that end give, in `resolveStepPoints`. (A connector's
 * points are local to its node, so it needs none of this.)
 */
export function routeAt(points: LinePoint[], g: LineGeometry): LinePoint[] {
  const frame = g.pointsFrame;
  if (!frame) return points;
  const ds = { x: g.source.x - frame.source.x, y: g.source.y - frame.source.y };
  const dt = { x: g.target.x - frame.target.x, y: g.target.y - frame.target.y };
  if (Math.abs(ds.x - dt.x) > 0.5 || Math.abs(ds.y - dt.y) > 0.5) return points;
  if (ds.x === 0 && ds.y === 0) return points;
  return points.map((p) => ({ ...p, x: p.x + ds.x, y: p.y + ds.y }));
}

export function routeFrame(g: LineGeometry): LineRouteFrame {
  return {
    source: { x: g.source.x, y: g.source.y },
    target: { x: g.target.x, y: g.target.y },
  };
}

/**
 * The route as it stands now, ready to store again with a fresh frame. Run
 * after nodes moved: the next joint move then translates from here, and the
 * next single move adjusts only the segments nearest the node that moved.
 */
export function settledRouteNow(
  g: LineGeometry,
): { points: LinePoint[]; points_frame: LineRouteFrame } | null {
  if (!g.points || g.points.length === 0) return null;
  return {
    points: resolveStepPoints(routeAt(g.points, g), g),
    points_frame: routeFrame(g),
  };
}

/**
 * The points to store when a drag ends: duplicates fold into one, a point
 * that only continues a straight run goes (the stubs always stay), and a
 * route that landed back on the automatic one is stored as none at all, so
 * the line is automatic again — one bar, and it follows its nodes freely.
 */
export function settleStepPoints(
  g: LineGeometry,
  points: LinePoint[],
): LinePoint[] | null {
  const tidy = tidyStepPoints(points);
  // The automatic route is compared by shape: it may carry a corner that
  // only continues a straight run (two nodes level with each other).
  const auto = tidyStepPoints(getStepInitialPoints(g).slice(1, -1));
  const automatic =
    tidy.length === auto.length &&
    tidy.every((p, i) => p.x === auto[i].x && p.y === auto[i].y);
  return automatic ? null : tidy;
}

/** Duplicates fold into one; a point that only continues a run goes. */
function tidyStepPoints(points: LinePoint[]): LinePoint[] {
  return points
    .filter(
      (p, i) => i === 0 || p.x !== points[i - 1].x || p.y !== points[i - 1].y,
    )
    .filter((p, i, all) => {
      if (i === 0 || i === all.length - 1) return true;
      const prev = all[i - 1];
      const next = all[i + 1];
      const collinear =
        (prev.x === p.x && p.x === next.x) ||
        (prev.y === p.y && p.y === next.y);
      return !collinear;
    });
}

/**
 * Where a dragged through-point settles: back on the straight line when it
 * is nearly there (no bend), else lined up with the ends where it is close,
 * else on the grid.
 */
export function snapBend(
  g: LineGeometry,
  bend: XYPosition,
  tolerance: number,
  grid: number,
): XYPosition | null {
  if (Math.hypot(bend.x, bend.y) < tolerance) return null;
  const mid = {
    x: (g.source.x + g.target.x) / 2,
    y: (g.source.y + g.target.y) / 2,
  };
  const settle = (axis: "x" | "y") => {
    const value = mid[axis] + bend[axis];
    const guides = [g.source[axis], g.target[axis], mid[axis]];
    let best = value;
    let bestDist = tolerance;
    for (const guide of guides) {
      const d = Math.abs(guide - value);
      if (d < bestDist) {
        bestDist = d;
        best = guide;
      }
    }
    return (
      (best !== value ? best : Math.round(value / grid) * grid) - mid[axis]
    );
  };
  return { x: settle("x"), y: settle("y") };
}

// --- Elbow internals (React Flow Pro editable-edge, ported) ---------------

const SIDE_DIR: Record<Position, XYPosition> = {
  [Position.Left]: { x: -1, y: 0 },
  [Position.Right]: { x: 1, y: 0 },
  [Position.Top]: { x: 0, y: -1 },
  [Position.Bottom]: { x: 0, y: 1 },
};

function direction(
  source: XYPosition,
  sourceSide: Position,
  target: XYPosition,
): XYPosition {
  if (sourceSide === Position.Left || sourceSide === Position.Right) {
    return source.x < target.x ? { x: 1, y: 0 } : { x: -1, y: 0 };
  }
  return source.y < target.y ? { x: 0, y: 1 } : { x: 0, y: -1 };
}

let seq = 0;
function pointId(): string {
  seq += 1;
  return `p${Date.now().toString(36)}${seq}`;
}

/**
 * The points React Flow's smooth-step would route through, so an untouched
 * elbow and a just-touched one start from the same polyline. Includes the
 * source and target as first and last entries.
 */
export function getStepInitialPoints(g: LineGeometry): LinePoint[] {
  const { source, target } = g;
  const sDir = SIDE_DIR[source.side];
  const tDir = SIDE_DIR[target.side];
  const sGap = {
    x: source.x + sDir.x * STEP_OFFSET,
    y: source.y + sDir.y * STEP_OFFSET,
  };
  const tGap = {
    x: target.x + tDir.x * STEP_OFFSET,
    y: target.y + tDir.y * STEP_OFFSET,
  };
  const dir = direction(sGap, source.side, tGap);
  const axis: "x" | "y" = dir.x !== 0 ? "x" : "y";
  const currDir = dir[axis];

  let points: XYPosition[] = [];
  const sOff = { x: 0, y: 0 };
  const tOff = { x: 0, y: 0 };
  const centerX = (source.x + target.x) / 2;
  const centerY = (source.y + target.y) / 2;

  if (sDir[axis] * tDir[axis] === -1) {
    // Opposite sides: one split in the middle.
    const vertical = [
      { x: centerX, y: sGap.y },
      { x: centerX, y: tGap.y },
    ];
    const horizontal = [
      { x: sGap.x, y: centerY },
      { x: tGap.x, y: centerY },
    ];
    if (sDir[axis] === currDir) {
      points = axis === "x" ? vertical : horizontal;
    } else {
      points = axis === "x" ? horizontal : vertical;
    }
  } else {
    const sourceTarget = [{ x: sGap.x, y: tGap.y }];
    const targetSource = [{ x: tGap.x, y: sGap.y }];
    if (axis === "x") {
      points = sDir.x === currDir ? targetSource : sourceTarget;
    } else {
      points = sDir.y === currDir ? sourceTarget : targetSource;
    }
    if (source.side === target.side) {
      const diff = Math.abs(source[axis] - target[axis]);
      if (diff <= STEP_OFFSET) {
        const gap = Math.min(STEP_OFFSET - 1, STEP_OFFSET - diff);
        if (sDir[axis] === currDir) {
          sOff[axis] = (sGap[axis] > source[axis] ? -1 : 1) * gap;
        } else {
          tOff[axis] = (tGap[axis] > target[axis] ? -1 : 1) * gap;
        }
      }
    }
    if (source.side !== target.side) {
      const other: "x" | "y" = axis === "x" ? "y" : "x";
      const sameDir = sDir[axis] === tDir[other];
      const sGt = sGap[other] > tGap[other];
      const sLt = sGap[other] < tGap[other];
      const flip =
        (sDir[axis] === 1 && ((!sameDir && sGt) || (sameDir && sLt))) ||
        (sDir[axis] !== 1 && ((!sameDir && sLt) || (sameDir && sGt)));
      if (flip) points = axis === "x" ? sourceTarget : targetSource;
    }
  }

  const all: XYPosition[] = [
    source,
    { x: sGap.x + sOff.x, y: sGap.y + sOff.y },
    ...points,
    { x: tGap.x + tOff.x, y: tGap.y + tOff.y },
    target,
  ];
  return all.map((p, i) => ({ x: p.x, y: p.y, id: `s${i}` }));
}

/**
 * The stored interior points as they should be drawn now: the stubs are
 * re-derived from where the ends are (a node move follows the user's bends
 * instead of throwing them away), then any pair of points left off-axis by
 * that gets a corner between them, so the line is orthogonal without fail —
 * what the bars and the label are computed from is what is drawn.
 */
export function resolveStepPoints(
  points: LinePoint[],
  g: LineGeometry,
): LinePoint[] {
  const out = points.map((p) => ({ ...p }));
  reseat(out, g.source, false);
  reseat(out, g.target, true);
  return orthogonalise(out, g);
}

function reseat(points: LinePoint[], end: LineEnd, isTarget: boolean) {
  const along: "x" | "y" =
    end.side === Position.Left || end.side === Position.Right ? "x" : "y";
  const across: "x" | "y" = along === "x" ? "y" : "x";
  const first = isTarget ? points.length - 1 : 0;
  const second = isTarget ? points.length - 2 : 1;
  const a = points[first];
  const b = points[second];
  if (!a || !b) return;
  const dir = SIDE_DIR[end.side];
  const gapped = dir[along] * STEP_OFFSET + end[along];
  if (a[across] !== b[across]) {
    a[along] = gapped;
    a[across] = end[across];
    b[along] = gapped;
  } else {
    a[across] = end[across];
    a[along] = gapped;
    b[across] = end[across];
  }
}

function orthogonalise(interior: LinePoint[], g: LineGeometry): LinePoint[] {
  const full: (XYPosition & { id?: string })[] = [
    g.source,
    ...interior,
    g.target,
  ];
  const out: LinePoint[] = [];
  // The leaving stub sets the first direction.
  let horizontal =
    g.source.side === Position.Left || g.source.side === Position.Right;
  for (let i = 0; i < full.length - 1; i++) {
    const a = full[i];
    const b = full[i + 1];
    if (i > 0) out.push(a as LinePoint);
    if (a.x !== b.x && a.y !== b.y) {
      // Off-axis: turn the corner, continuing the way the line was going.
      // The corner's id follows from its neighbours, so it is the same on
      // every render.
      const corner = horizontal
        ? { x: b.x, y: a.y, id: `${a.id ?? "s"}~${b.id ?? "t"}` }
        : { x: a.x, y: b.y, id: `${a.id ?? "s"}~${b.id ?? "t"}` };
      out.push(corner);
      horizontal = !horizontal;
    } else if (a.x !== b.x) {
      horizontal = true;
    } else if (a.y !== b.y) {
      horizontal = false;
    }
  }
  return out;
}

function bendAt(a: XYPosition, b: XYPosition, c: XYPosition): string {
  const size = 5;
  const bendSize = Math.min(dist(a, b) / 2, dist(b, c) / 2, size);
  const { x, y } = b;
  if ((a.x === x && x === c.x) || (a.y === y && y === c.y)) {
    return `L${x} ${y}`;
  }
  if (a.y === y) {
    const xDir = a.x < c.x ? -1 : 1;
    const yDir = a.y < c.y ? 1 : -1;
    return `L ${x + bendSize * xDir},${y}Q ${x},${y} ${x},${y + bendSize * yDir}`;
  }
  const xDir = a.x < c.x ? 1 : -1;
  const yDir = a.y < c.y ? -1 : 1;
  return `L ${x},${y + bendSize * yDir}Q ${x},${y} ${x + bendSize * xDir},${y}`;
}

function dist(a: XYPosition, b: XYPosition): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

export function getStepPath(points: XYPosition[]): string {
  return points.reduce((d, p, i) => {
    if (i > 0 && i < points.length - 1) {
      return d + bendAt(points[i - 1], p, points[i + 1]);
    }
    return d + `${i === 0 ? "M" : "L"}${p.x} ${p.y}`;
  }, "");
}
